/*    
<summary>
  This functional component "AddProductManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik } from "formik";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { integerRegex } from "../../../../constants/constants";
import { useStore } from "../../../../contexts/store-provider";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputMobileNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import { AddWalkInCustomerAccountSchema } from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import ResetPasswordWindowPopupHOC from "../../../../shared-components/popup/change-password/reset-password-window-popup-hoc";
import RegisterCustomerAlreadyExistsPopup from "../../../../shared-components/popup/confirm/register-customer-already-exists";
import { formatMessage } from "../../../../translations/format-message";
import ConfirmPopupHOC from "../../../../shared-components/popup/confirm/confirm-popup-hoc";
import ConfirmPopup from "../../../../shared-components/popup/confirm/confirm-popup";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  modalClosed: () => void;
  changeForm: (formNo: any) => void;
  confirmPopupToggleHandler: () => void;
  resetPasswordToggleHandler: () => void;
  getCustomerLoading: boolean;
  showConfirmPopup: boolean;
  onSubmitOverwriteForm: (data:any) => void;
  showResetPasswordPopup: boolean;
  initialValues: any;
}

const CreateWalkInCustomerAccountForm = (props: IProps) => {
  const {
    customerManagementStore,
  } = useStore();
  const {
    validateCustomerState,
    ValidateEasyCustomerService,
    resetValidateCustomerState,
    addUpdateCustomerState
  } = customerManagementStore;

  const [lastNameKana, setLastNameKana] = useState<string>("");
  const [firstNameKana, setFirstNameKana] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone1, setPhone1] = useState<string>("");
  const [isEmailOverwrite, setIsEmailOverwrite] = useState<boolean>(false);

  // useEffect(() => {
  //   if (validateCustomerState.success) {
  //     validatedSubmitHandler();
  //     resetValidateCustomerState();
  //   }
  // }, [validateCustomerState.success]);

  useEffect(() => {
    if (validateCustomerState.error) {
      if (
        validateCustomerState.error == "IsThereADifferentEmailIdForThisAccount"
      ) {
        props.confirmPopupToggleHandler();
      } else if (
        validateCustomerState.error == "LizeraCustomerAlreadyExist" ||
        validateCustomerState.error == "LizeraRegisteredCustomerAlreadyExists"
      ) {
        props.resetPasswordToggleHandler();
      } else {
        toast.error(formatMessage(validateCustomerState.error));
      }
      resetValidateCustomerState();
    }
  }, [validateCustomerState.error]);

  const validateSubmitHandler = (values: any) => {
    setLastNameKana(values.LastNameKana);
    setFirstNameKana(values.FirstNameKana);
    setEmail(values.Email);
    setGender(values.Gender);
    setPhone1(values.Phone1);
    let dataObj = {
      FirstNameKana: values.FirstNameKana,
      LastNameKana: values.LastNameKana,
      Gender: values.Gender,
      Email: values.Email ? values.Email.toLowerCase() : null,
      CustomerType: 0,
      RegisteredBy: 1,
      Phone1: toHalfWidth(values.Phone1),
    };
    props.submitHandler(dataObj);
  };

  const validatedSubmitHandler = () => {
    let dataObj = {
      FirstNameKana: firstNameKana,
      LastNameKana: lastNameKana,
      Gender: gender,
      Email: email ? email.toLowerCase() : null,
      CustomerType: 0,
      RegisteredBy: 1,
      Phone1: toHalfWidth(phone1),
    };
    props.submitHandler(dataObj);
  };

    useEffect(() => {
    if (addUpdateCustomerState.success) {
      toast.success(formatMessage("added_success"));
      // props.confirmPopupToggleHandler();
    }
  }, [addUpdateCustomerState.success]);

  return (
    <>
      {props.showResetPasswordPopup && (
        <RegisterCustomerAlreadyExistsPopup
          modalClosed={() => {
            props.modalClosed();
            props.resetPasswordToggleHandler();
          }}
          title={formatMessage("customer_registration")}
          message={formatMessage(
            validateCustomerState.error == "LizeraCustomerAlreadyExist"
              ? "lizera_customer_already_exists"
              : "lizera_registered_customer_already_exists"
          )}
          isLoading={validateCustomerState.inProgress || addUpdateCustomerState.inProgress }
        />
      )}

      {props.showConfirmPopup && (
        <ConfirmPopup
          isLoading={validateCustomerState.inProgress || addUpdateCustomerState.inProgress }
          title={formatMessage("confirmation_popup_title")}
          message={formatMessage("IsThereADifferentEmailIdForThisAccountInfo")}
          modalSubmit={() =>
            props.onSubmitOverwriteForm({
              FirstNameKana: firstNameKana,
              LastNameKana: lastNameKana,
              Gender: gender,
              Email: email ? email.toLowerCase() : null,
              CustomerType: 0,
              RegisteredBy: 1,
              Phone1: toHalfWidth(phone1),
            })
          }
          modalClosed={props.confirmPopupToggleHandler}
        />
      )}

      <Modal className="double-modal add-user-modal" show={true} centered>
        <Modal.Header>
          <Modal.Title>
            <FormattedMessage id="customer_details" />
            <button
              onClick={props.onModalClosed}
              disabled={props.getCustomerLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize={true}
          validationSchema={AddWalkInCustomerAccountSchema}
          validateOnBlur={false}
          onSubmit={async (values) => {
            const updatedValues = {
              Gender: values.Gender,
              LastNameKana: values.LastNameKana?.trim(),
              FirstNameKana: values.FirstNameKana?.trim(),
              Email: values.Email?.toLowerCase(),
              Phone1: toHalfWidth(values.Phone1),
            };
            validateSubmitHandler(updatedValues);

            // props.submitHandler(customerData);
          }}
        >
          {({ setFieldValue, values }) => {
            return (
              <Form className="user-form">
                {props.getCustomerLoading ? (
                  <FormLoader loading={props.getCustomerLoading} />
                ) : null}
                <Modal.Body>
                  {/* Row 1: Last Name, First Name */}
                  <div className="row">
                    <div className="col-12 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="last_name_kana"
                      />
                      <Field
                        name="LastNameKana"
                        type="text"
                        className="form-control"
                        // regex={stringJpRegex}
                        as={FormikFormInput}
                        placeholder="enter_last_name_kana"
                      />
                    </div>
                    <div className="col-12 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="first_name_kana"
                      />
                      <Field
                        name="FirstNameKana"
                        type="text"
                        className="form-control"
                        // regex={stringJpRegex}
                        as={FormikFormInput}
                        placeholder="enter_first_name_kana"
                      />
                    </div>

                    <div className="col-12 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="email"
                      />
                      <Field
                        className="form-control"
                        name="Email"
                        type="email"
                        autoComplete="new-email"
                        placeholder="enter_email"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        as={FormikFormInput}
                        disabled={props.id > 0 ? true : false}
                      />
                    </div>
                    <div className="col-12 form-group">
                      <Label
                        className="label-style"
                        label="gender"
                        required={false}
                      />
                      <div className="row">
                        <div className="col-sm-4">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="0"
                            id="male"
                          />
                          <label className="form-check-label" htmlFor="male">
                            <FormattedMessage id="male" />
                          </label>
                        </div>
                        <div className="col-sm-4">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="1"
                            id="female"
                          />
                          <label className="form-check-label" htmlFor="female">
                            <FormattedMessage id="female" />
                          </label>
                        </div>
                        <div className="col-sm-4">
                          <Field
                            className="form-check-input"
                            name="Gender"
                            type="radio"
                            value="2"
                            id="not_set"
                          />
                          <label className="form-check-label" htmlFor="not_set">
                            <FormattedMessage id="not_set" />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 form-group">
                      <Label
                        className="label-style"
                        required={true}
                        label="contact_number"
                      />
                      <Field
                        className="form-control"
                        name="Phone1"
                        type="text"
                        placeholder="enter_mobile_number"
                        regex={integerRegex}
                        as={FormikFormInputMobileNumber}
                      />
                    </div>
                  </div>
                </Modal.Body>

                {/* Footer Buttons */}
                <div className="modal-footer d-flex align-items-center justify-content-center gap-0 border-0">
                  {/* <Button
                    variant="outline-primary"
                    type="button"
                    onClick={() => props.changeForm(1)}
                  >
                    <FormattedMessage id="login_book" />
                  </Button>
                  <Button
                    variant="btn btn-secondary"
                    onClick={() => props.changeForm(3)}
                  >
                    <FormattedMessage id="register_customer" />
                  </Button> */}
                  <LoaderButton
                    type="submit"
                    isLoading={props.getCustomerLoading}
                    text={"create_book"}
                  />
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default ConfirmPopupHOC(ResetPasswordWindowPopupHOC(
  observer(CreateWalkInCustomerAccountForm))
);
